// =============================================================================
// notesToHankMessageUtils.ts — Builder and parser functions for NTH messages
// =============================================================================

import type {
  NoteMessage,
  PhotoMetadata,
  ConversationContextEntry,
} from './NotesToHankTypes';

// -----------------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------------

export function buildInitialMessage(
  dealId: string,
  text: string,
  photos: PhotoMetadata[],
  conversationContext?: ConversationContextEntry[]
): NoteMessage {
  const msg: NoteMessage = {
    type: 'note_message',
    event_type: 'initial',
    deal_id: dealId,
    text,
    photos,
    timestamp: Date.now(),
  };

  if (conversationContext && conversationContext.length > 0) {
    msg.conversation_context = conversationContext;
  }

  return msg;
}

export function buildFollowUpMessage(
  dealId: string,
  text: string,
  photos: PhotoMetadata[],
  selectedAnswerIds: string[],
  pressedActionButtonIds: string[]
): NoteMessage {
  return {
    type: 'note_message',
    event_type: 'follow_up',
    deal_id: dealId,
    text,
    photos,
    selected_answer_ids: selectedAnswerIds,
    pressed_action_button_ids: pressedActionButtonIds,
    timestamp: Date.now(),
  };
}

// -----------------------------------------------------------------------------
// Parser
// -----------------------------------------------------------------------------

export function parseInboundMessage(raw: string): NoteMessage | null {
  try {
    const parsed = JSON.parse(raw);

    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      return null;
    }

    if (parsed.type !== 'note_message') {
      return null;
    }

    const validEventTypes = ['initial', 'follow_up', 'response', 'error'];
    if (!validEventTypes.includes(parsed.event_type)) {
      return null;
    }

    if (typeof parsed.deal_id !== 'string') {
      return null;
    }

    return parsed as NoteMessage;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// UI helpers
// -----------------------------------------------------------------------------

export function isSendEnabled(
  text: string,
  selectedAnswerIds: string[],
  pressedActionButtonIds: string[]
): boolean {
  return (
    text.trim().length > 0 ||
    selectedAnswerIds.length > 0 ||
    pressedActionButtonIds.length > 0
  );
}
